import { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store';
import {
  iapInitRequest,
  iapPurchaseRequest,
  iapVerifyPurchaseRequest,
  iapReset,
  iapDisconnect,
} from '../../store/iap/iapActions';
import { Package } from '../../store/types';
import { iapService } from '../../services/iap';

export function useIAP() {
  const dispatch = useDispatch();
  const iapState = useSelector((state: RootState) => state.iap);

  /**
   * Initialize IAP on mount
   */
  useEffect(() => {
    if (iapService.isIAPSupported() && !iapState.isInitialized) {
      dispatch(iapInitRequest());
    }

    return () => {
      // Cleanup on unmount
      if (iapService.isIAPSupported()) {
        dispatch(iapDisconnect());
      }
    };
  }, [dispatch]);

  /**
   * Auto-verify purchases when they come in
   */
  useEffect(() => {
    if (iapState.currentPurchase && iapState.currentPackage && !iapState.verifyLoading && !iapState.purchaseVerified) {
      dispatch(
        iapVerifyPurchaseRequest({
          purchase: iapState.currentPurchase,
          pkg: iapState.currentPackage,
        })
      );
    }
  }, [iapState.currentPurchase, iapState.currentPackage, iapState.verifyLoading, iapState.purchaseVerified, dispatch]);

  /**
   * Purchase a package
   * Product ID is extracted from pkg.metadata.applePay or pkg.metadata.googlePay
   */
  const purchasePackage = useCallback(
    (pkg: Package) => {
      const productId = iapService.mapPackageToProductId(pkg);

      if (!productId) {
        console.error('[useIAP] Could not get product ID from package metadata:', pkg);
        console.error('[useIAP] Package must have metadata.applePay (iOS) or metadata.googlePay (Android)');
        return;
      }

      const isSubscription = pkg.type === 'VIP_MEMBERSHIP';

      console.log('[useIAP] Purchasing package:', {
        packageId: pkg.id,
        packageName: pkg.name,
        productId,
        isSubscription,
      });

      dispatch(
        iapPurchaseRequest({
          productId,
          pkg,
          isSubscription,
        })
      );
    },
    [dispatch]
  );

  /**
   * Reset IAP state
   */
  const resetIAP = useCallback(() => {
    dispatch(iapReset());
  }, [dispatch]);

  /**
   * Check if IAP is supported on this platform
   */
  const isSupported = iapService.isIAPSupported();

  return {
    // State
    isSupported,
    isInitialized: iapState.isInitialized,
    initLoading: iapState.initLoading,
    products: iapState.products,
    subscriptions: iapState.subscriptions,
    productsLoading: iapState.productsLoading,
    subscriptionsLoading: iapState.subscriptionsLoading,
    purchaseLoading: iapState.purchaseLoading,
    purchaseError: iapState.purchaseError,
    verifyLoading: iapState.verifyLoading,
    purchaseVerified: iapState.purchaseVerified,
    newBalance: iapState.newBalance,
    currentPurchase: iapState.currentPurchase,
    currentPackage: iapState.currentPackage,

    // Actions
    purchasePackage,
    resetIAP,
  };
}
